import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { apiFetch, parseJsonSafe } from '../utils/api';
import { calculateBudgetTotals, formatCurrency, formatDate, previewFile } from '../utils/invoiceUtils';

const todayStr = () => new Date().toISOString().slice(0, 10);

const formatPeriod = (start, end) => {
  if (!start && !end) return '-';
  return `${formatDate(start)} - ${formatDate(end)}`;
};

const formatFileSize = (value) => {
  if (!value && value !== 0) return '';
  const size = Number(value);
  if (Number.isNaN(size)) return '';
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let idx = -1;
  let num = size;
  do { num /= 1024; idx += 1; } while (num >= 1024 && idx < units.length - 1);
  return `${num.toFixed(num >= 10 ? 0 : 1)} ${units[idx]}`;
};

const emptyLineForm = { id: null, concepto: '', cantidad: 1, precio: 0, descuento: 0, tvaTx: 21, periodStart: '', periodEnd: '' };

const BudgetFormPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const budgetId = Number.isFinite(Number(id)) ? Number(id) : null;
  const clientIdRaw = searchParams.get('clientId');
  const clientIdParam = clientIdRaw && Number.isFinite(Number(clientIdRaw)) ? Number(clientIdRaw) : null;
  const clientLocked = clientIdParam !== null;
  const backTo = clientIdParam ? `/clientes/${clientIdParam}` : '/presupuestos';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [numberLoading, setNumberLoading] = useState(false);
  const [savingBudget, setSavingBudget] = useState(false);
  const [loadingBudgetLines, setLoadingBudgetLines] = useState(false);
  const [budgetLinesLoaded, setBudgetLinesLoaded] = useState(false);
  const [budgetLines, setBudgetLines] = useState([]);
  const [budgetLineForm, setBudgetLineForm] = useState(emptyLineForm);
  const [budgetForm, setBudgetForm] = useState({
    clientId: clientIdParam || null,
    clientLabel: '',
    id: budgetId || null,
    presupuesto: '',
    fecha: todayStr(),
    validUntil: '',
    estado: 'Borrador',
    canal: 'Manual',
    footerText: '',
    baseImponible: '',
    iva: '',
    total: ''
  });
  const [clientOptions, setClientOptions] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [templates, setTemplates] = useState([]);
  const [emailModal, setEmailModal] = useState({ open: false, sending: false, error: '', to: '', subject: '', templateId: '', bodyHtml: '' });
  const [whatsappModal, setWhatsappModal] = useState({ open: false, sending: false, error: '', phone: '' });

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  }, []);

  const previewPdf = async (endpoint) => {
    try { await previewFile(endpoint, token, handleUnauthorized); }
    catch (err) { alert(err.message); }
  };

  const downloadFile = async (endpoint, filename) => {
    try {
      const res = await apiFetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        throw new Error((data && data.message) || 'Error descargando archivo');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'presupuesto.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  const fetchClients = useCallback(async (search = '') => {
    setClientsLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, pageSize: 20 });
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/clients?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando clientes');
      setClientOptions((data?.data || []).map((c) => ({
        value: c.id,
        label: `${c.razon_social || `Cliente #${c.id}`}${c.email ? ` · ${c.email}` : ''}`,
        data: c
      })));
    } catch (err) {
      console.error(err.message);
    } finally {
      setClientsLoading(false);
    }
  }, [token, handleUnauthorized]);

  const fetchClientSummary = useCallback(async (clientId) => {
    if (!clientId) return;
    try {
      const res = await apiFetch(`/api/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando cliente');
      const label = `${data?.razon_social || `Cliente #${clientId}`}${data?.email ? ` · ${data.email}` : ''}`;
      setBudgetForm((prev) => ({ ...prev, clientId, clientLabel: label }));
    } catch (err) {
      console.error(err.message);
    }
  }, [token, handleUnauthorized]);

  const fetchNextBudgetNumber = useCallback(async () => {
    setNumberLoading(true);
    try {
      const res = await apiFetch('/api/budgets/next-number', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error generando número');
      setBudgetForm((prev) => ({ ...prev, presupuesto: data.number || prev.presupuesto }));
    } catch (err) {
      console.error(err.message);
    } finally {
      setNumberLoading(false);
    }
  }, [token, handleUnauthorized]);

  const fetchBudget = useCallback(async (budgetIdValue) => {
    if (!budgetIdValue) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/budgets/${budgetIdValue}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo presupuesto');
      setReadOnly(Boolean(data?.dolibarr_proposal_id));
      setBudgetForm({
        clientId: data?.client_id || null,
        clientLabel: data?.razon_social || '',
        id: data?.id || null,
        presupuesto: data?.presupuesto || '',
        fecha: data?.fecha ? data.fecha.substring(0, 10) : todayStr(),
        validUntil: data?.valid_until ? String(data.valid_until).substring(0, 10) : '',
        estado: data?.estado || 'Borrador',
        canal: data?.canal || 'Manual',
        footerText: data?.footer_text || '',
        baseImponible: data?.base_imponible ?? '',
        iva: data?.iva ?? '',
        total: data?.total ?? '',
        convertedInvoiceId: data?.converted_invoice_id || null
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, handleUnauthorized]);

  const fetchBudgetLines = useCallback(async (budgetIdValue) => {
    if (!budgetIdValue) return;
    setLoadingBudgetLines(true);
    setBudgetLinesLoaded(false);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budgetIdValue}/lines`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo líneas');
      setBudgetLines(Array.isArray(data) ? data : []);
      setBudgetLinesLoaded(true);
    } catch (err) {
      alert(err.message);
      setBudgetLines([]);
      setBudgetLinesLoaded(false);
    } finally {
      setLoadingBudgetLines(false);
    }
  }, [token, handleUnauthorized]);

  const fetchAttachments = useCallback(async (budgetIdValue) => {
    if (!budgetIdValue) return;
    setAttachmentsLoading(true);
    setAttachmentsError('');
    setAttachments([]);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budgetIdValue}/attachments`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo adjuntos');
      setAttachments(Array.isArray(data) ? data : []);
    } catch (err) {
      setAttachments([]);
      setAttachmentsError(err.message);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [token, handleUnauthorized]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await apiFetch('/api/email/templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok) setTemplates(Array.isArray(data) ? data : []);
    } catch (_) { /* noop */ }
  }, [token]);

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !budgetForm.id) return;
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch(`/api/clients/budgets/${budgetForm.id}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error subiendo adjunto');
      await fetchAttachments(budgetForm.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  const toggleQuotePdf = async (attachment) => {
    const next = !attachment.is_quote_pdf;
    setMarkingId(attachment.id);
    try {
      const res = await apiFetch(`/api/clients/budget-attachments/${attachment.id}/quote-pdf`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_quote_pdf: next })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar el adjunto');
      setAttachments(Array.isArray(data) ? data : []);
    } catch (err) {
      alert(err.message);
    } finally {
      setMarkingId(null);
    }
  };

  const deleteAttachment = async (attachmentId) => {
    if (!window.confirm('¿Eliminar adjunto?')) return;
    try {
      const res = await apiFetch(`/api/clients/budget-attachments/${attachmentId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error eliminando adjunto');
      if (budgetForm.id) fetchAttachments(budgetForm.id);
    } catch (err) {
      alert(err.message);
    }
  };

  const buildBudgetBody = () => ({
    presupuesto: budgetForm.presupuesto,
    fecha: budgetForm.fecha || null,
    validUntil: budgetForm.validUntil || null,
    estado: budgetForm.estado,
    canal: budgetForm.canal,
    footerText: budgetForm.footerText || '',
    baseImponible: budgetForm.baseImponible || null,
    iva: budgetForm.iva || null,
    total: budgetForm.total || null
  });

  const saveBudget = async () => {
    if (readOnly) return navigate(backTo);
    if (!budgetForm.clientId) return alert('Selecciona un cliente');
    if (!budgetForm.presupuesto) return alert('El número de presupuesto es obligatorio');
    const linesKnown = !budgetForm.id || budgetLinesLoaded;
    if (!budgetLines.length && linesKnown && ['Aceptado', 'Rechazado'].includes(budgetForm.estado)) {
      return alert('Añade al menos una línea antes de marcar el presupuesto como Aceptado o Rechazado.');
    }
    setSavingBudget(true);
    try {
      const isEdit = Boolean(budgetForm.id);
      const res = await apiFetch(isEdit ? `/api/clients/budgets/${budgetForm.id}` : `/api/clients/${budgetForm.clientId}/budgets`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(buildBudgetBody())
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      if (!isEdit && data?.id) {
        const suffix = clientIdParam ? `?clientId=${clientIdParam}` : '';
        navigate(`/presupuestos/${data.id}${suffix}`, { replace: true });
        return;
      }
      setBudgetForm((prev) => ({
        ...prev,
        presupuesto: data?.presupuesto || prev.presupuesto,
        fecha: data?.fecha ? data.fecha.substring(0, 10) : prev.fecha,
        validUntil: data?.valid_until ? String(data.valid_until).substring(0, 10) : prev.validUntil,
        estado: data?.estado || prev.estado
      }));
      setActionMsg('Presupuesto guardado');
      setTimeout(() => setActionMsg(''), 2500);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingBudget(false);
    }
  };

  const ensureBudgetCreated = async () => {
    if (budgetForm.id) return budgetForm.id;
    if (!budgetForm.clientId) { alert('Selecciona un cliente'); return null; }
    const res = await apiFetch(`/api/clients/${budgetForm.clientId}/budgets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(buildBudgetBody())
    });
    const data = await parseJsonSafe(res);
    if (res.status === 401) { handleUnauthorized(); return null; }
    if (!res.ok) { alert(data.message || 'No se pudo crear el presupuesto'); return null; }
    const newId = data?.id || null;
    if (newId) {
      const suffix = clientIdParam ? `?clientId=${clientIdParam}` : '';
      navigate(`/presupuestos/${newId}${suffix}`, { replace: true });
      setBudgetForm((prev) => ({
        ...prev,
        id: newId,
        presupuesto: data?.presupuesto || prev.presupuesto,
        fecha: data?.fecha ? data.fecha.substring(0, 10) : prev.fecha,
        validUntil: data?.valid_until ? String(data.valid_until).substring(0, 10) : prev.validUntil,
        estado: data?.estado || prev.estado
      }));
    }
    return newId;
  };

  const [duplicatingLine, setDuplicatingLine] = useState(null);
  const saveBudgetLine = async () => {
    if (!budgetLineForm.concepto) return alert('Concepto obligatorio');
    const targetId = await ensureBudgetCreated();
    if (!targetId) return;
    const isEdit = Boolean(budgetLineForm.id);
    const url = isEdit
      ? `/api/clients/budgets/${targetId}/lines/${budgetLineForm.id}`
      : `/api/clients/budgets/${targetId}/lines`;
    try {
      const res = await apiFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          concepto: budgetLineForm.concepto,
          cantidad: budgetLineForm.cantidad,
          precio: budgetLineForm.precio,
          descuento: budgetLineForm.descuento,
          tvaTx: budgetLineForm.tvaTx,
          periodStart: budgetLineForm.periodStart || null,
          periodEnd: budgetLineForm.periodEnd || null
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error guardando línea');
      setBudgetLineForm(emptyLineForm);
      fetchBudgetLines(targetId);
    } catch (err) {
      alert(err.message);
    }
  };

  const duplicateLine = async (line) => {
    if (duplicatingLine) return;
    setDuplicatingLine(line.id);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budgetForm.id}/lines`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ concepto: line.concepto, cantidad: line.cantidad, precio: line.precio, descuento: line.descuento || 0, tvaTx: line.tva_tx ?? 21, periodStart: line.period_start?.slice(0, 10) || null, periodEnd: line.period_end?.slice(0, 10) || null })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo duplicar la línea');
      await fetchBudgetLines(budgetForm.id);
    } catch (err) { alert(err.message); }
    finally { setDuplicatingLine(null); }
  };

  const editBudgetLine = (line) => {
    setBudgetLineForm({
      id: line.id,
      concepto: line.concepto,
      cantidad: Number(line.cantidad || 1),
      precio: Number(line.precio || 0),
      descuento: Number(line.descuento || 0),
      tvaTx: line.tva_tx != null ? Number(line.tva_tx) : 21,
      periodStart: line.period_start ? line.period_start.substring(0, 10) : '',
      periodEnd: line.period_end ? line.period_end.substring(0, 10) : ''
    });
  };

  const deleteBudgetLine = async (lineId) => {
    if (!budgetForm.id) return;
    if (!window.confirm('¿Eliminar esta línea?')) return;
    try {
      const res = await apiFetch(`/api/clients/budgets/${budgetForm.id}/lines/${lineId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error eliminando línea');
      fetchBudgetLines(budgetForm.id);
    } catch (err) {
      alert(err.message);
    }
  };

  const duplicateBudget = async () => {
    if (!budgetForm.id) return;
    try {
      const res = await apiFetch(`/api/clients/budgets/${budgetForm.id}/duplicate`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo duplicar');
      navigate(`/presupuestos/${data.id}`);
    } catch (err) {
      alert(err.message);
    }
  };

  const convertToInvoice = async () => {
    if (!budgetForm.id) return;
    if (!window.confirm('¿Convertir este presupuesto en factura (borrador)?')) return;
    try {
      const res = await apiFetch(`/api/clients/budgets/${budgetForm.id}/convert-to-invoice`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo convertir');
      navigate(`/facturas/${data.id}`);
    } catch (err) {
      alert(err.message);
    }
  };

  const openEmailModal = async () => {
    const defaultTemplateId = templates[0]?.id ? String(templates[0].id) : '';
    const template = templates.find((t) => String(t.id) === defaultTemplateId);
    let to = '';
    if (budgetForm.clientId) {
      try {
        const res = await apiFetch(`/api/clients/${budgetForm.clientId}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.ok) to = data.email || '';
      } catch (_) { /* noop */ }
    }
    setEmailModal({ open: true, sending: false, error: '', to, subject: `Presupuesto ${budgetForm.presupuesto || ''}`.trim(), templateId: defaultTemplateId, bodyHtml: template?.body_html || '' });
  };

  const sendEmail = async () => {
    if (!emailModal.to.trim()) return setEmailModal((p) => ({ ...p, error: 'El email es obligatorio.' }));
    setEmailModal((p) => ({ ...p, sending: true, error: '' }));
    try {
      const res = await apiFetch(`/api/clients/budgets/${budgetForm.id}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: emailModal.to, subject: emailModal.subject, html: emailModal.bodyHtml, templateId: emailModal.templateId || undefined })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar por email');
      setEmailModal((p) => ({ ...p, sending: false, open: false }));
      if (budgetForm.id) fetchBudget(budgetForm.id);
      setActionMsg('Presupuesto enviado por email');
      setTimeout(() => setActionMsg(''), 2500);
    } catch (err) {
      setEmailModal((p) => ({ ...p, sending: false, error: err.message }));
    }
  };

  const openWhatsappModal = async () => {
    let phone = '';
    if (budgetForm.clientId) {
      try {
        const res = await apiFetch(`/api/clients/${budgetForm.clientId}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.ok) phone = data.telefono || '';
      } catch (_) { /* noop */ }
    }
    setWhatsappModal({ open: true, sending: false, error: '', phone });
  };

  const sendWhatsapp = async () => {
    if (!whatsappModal.phone.trim()) return setWhatsappModal((p) => ({ ...p, error: 'El teléfono es obligatorio.' }));
    setWhatsappModal((p) => ({ ...p, sending: true, error: '' }));
    try {
      const res = await apiFetch(`/api/clients/budgets/${budgetForm.id}/send-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar por WhatsApp');
      setWhatsappModal((p) => ({ ...p, sending: false, open: false }));
      if (budgetForm.id) fetchBudget(budgetForm.id);
      setActionMsg('Presupuesto enviado por WhatsApp');
      setTimeout(() => setActionMsg(''), 2500);
    } catch (err) {
      setWhatsappModal((p) => ({ ...p, sending: false, error: err.message }));
    }
  };

  const selectedClientOption = useMemo(() => {
    const found = clientOptions.find((opt) => opt.value === budgetForm.clientId);
    if (found) return found;
    if (budgetForm.clientId) return { value: budgetForm.clientId, label: budgetForm.clientLabel || `Cliente #${budgetForm.clientId}` };
    return null;
  }, [clientOptions, budgetForm.clientId, budgetForm.clientLabel]);

  const totals = useMemo(() => calculateBudgetTotals(budgetLines), [budgetLines]);
  const rateKeys = useMemo(() => Object.keys(totals.byRate || {}).map(Number).sort((a, b) => b - a), [totals]);

  const lineFormTotals = useMemo(() => {
    const gross = Number(budgetLineForm.cantidad || 0) * Number(budgetLineForm.precio || 0);
    const net = gross * (1 - Number(budgetLineForm.descuento || 0) / 100);
    return { net: Number.isNaN(net) ? 0 : net };
  }, [budgetLineForm]);

  const renderBudgetLinesTable = (showActions) => {
    if (!budgetLines.length) return <div className="text-muted small">Sin líneas.</div>;
    return (
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Concepto</th>
              <th>Periodo</th>
              <th className="text-end">Cant.</th>
              <th className="text-end">Precio</th>
              <th className="text-end">Dto.</th>
              <th className="text-end">IVA</th>
              <th className="text-end">Subtotal</th>
              {showActions && <th></th>}
            </tr>
          </thead>
          <tbody>
            {budgetLines.map((line) => {
              const gross = Number(line.total ?? (Number(line.cantidad) * Number(line.precio)));
              const net = gross * (1 - Number(line.descuento || 0) / 100);
              return (
                <tr key={line.id}>
                  <td>{line.concepto}</td>
                  <td className="small text-muted">{formatPeriod(line.period_start, line.period_end)}</td>
                  <td className="text-end">{line.cantidad}</td>
                  <td className="text-end">{formatCurrency(line.precio)}</td>
                  <td className="text-end">{Number(line.descuento || 0) > 0 ? `${Number(line.descuento)}%` : '-'}</td>
                  <td className="text-end">{line.tva_tx != null ? `${Number(line.tva_tx)}%` : '21%'}</td>
                  <td className="text-end">{formatCurrency(net)}</td>
                  {showActions && (
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-secondary me-2" type="button" disabled={Boolean(duplicatingLine)} onClick={() => duplicateLine(line)}>{duplicatingLine === line.id ? 'Duplicando…' : 'Duplicar'}</button>
                      <button className="btn btn-sm btn-outline-secondary me-2" type="button" onClick={() => editBudgetLine(line)}>Editar</button>
                      <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => deleteBudgetLine(line.id)}>Eliminar</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  useEffect(() => {
    if (budgetId) {
      fetchBudget(budgetId);
      fetchBudgetLines(budgetId);
      fetchAttachments(budgetId);
      return;
    }
    if (clientIdParam) fetchClientSummary(clientIdParam);
    fetchNextBudgetNumber();
  }, [budgetId, clientIdParam, fetchAttachments, fetchBudget, fetchBudgetLines, fetchClientSummary, fetchNextBudgetNumber]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (budgetId || readOnly || clientLocked) return;
    const query = clientQuery.trim();
    const handler = setTimeout(() => fetchClients(query), 250);
    return () => clearTimeout(handler);
  }, [clientQuery, budgetId, readOnly, clientLocked, fetchClients]);

  // Mantén los totales de cabecera en sincronía con las líneas (no para Dolibarr)
  useEffect(() => {
    if (!budgetLinesLoaded || readOnly) return;
    // No sobrescribir los totales de cabecera con 0 si el presupuesto no tiene líneas de detalle
    // (p. ej. presupuestos importados de Dolibarr con totales pero sin líneas).
    if (!budgetLines.length) return;
    const nextBase = totals.subtotalValue.toFixed(2);
    const nextIva = totals.ivaValue.toFixed(2);
    const nextTotal = totals.totalValue.toFixed(2);
    setBudgetForm((prev) => {
      if (prev.baseImponible === nextBase && prev.iva === nextIva && prev.total === nextTotal) return prev;
      return { ...prev, baseImponible: nextBase, iva: nextIva, total: nextTotal };
    });
  }, [totals, budgetLinesLoaded, readOnly, budgetLines.length]);

  const displayTotals = readOnly
    ? { subtotalValue: Number(budgetForm.baseImponible || 0), ivaValue: Number(budgetForm.iva || 0), totalValue: Number(budgetForm.total || 0), byRate: {} }
    : totals;

  return (
    <div className="small">
      <div className="d-flex flex-wrap justify-content-end gap-3 border rounded bg-white p-2 shadow-sm" style={{ position: 'sticky', top: 0, zIndex: 1010 }} aria-label="Totales del presupuesto">
        <span>Base: <strong>{formatCurrency(displayTotals.subtotalValue)}</strong></span><span>IVA: <strong>{formatCurrency(displayTotals.ivaValue)}</strong></span><span>Total: <strong>{formatCurrency(displayTotals.totalValue)}</strong></span>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">{budgetForm.id ? (readOnly ? 'Presupuesto (solo lectura)' : 'Editar presupuesto') : 'Nuevo presupuesto'}</h2>
          <div className="text-muted small">{budgetForm.clientLabel || (budgetForm.clientId ? `Cliente #${budgetForm.clientId}` : 'Selecciona un cliente')}</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(backTo)}>Volver</button>
          {!readOnly && (
            <button className="btn btn-primary btn-sm" onClick={saveBudget} disabled={savingBudget}>
              {savingBudget ? 'Guardando...' : budgetForm.id ? 'Actualizar' : 'Guardar'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {actionMsg && <div className="alert alert-success py-2">{actionMsg}</div>}

      {readOnly && (
        <div className="alert alert-warning py-2">Este presupuesto está sincronizado desde Dolibarr. Solo puedes verlo aquí; edítalo desde Dolibarr.</div>
      )}
      {budgetForm.convertedInvoiceId && (
        <div className="alert alert-info py-2 d-flex justify-content-between align-items-center">
          <span>Este presupuesto ya se convirtió en factura.</span>
          <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/facturas/${budgetForm.convertedInvoiceId}`)}>Ver factura</button>
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          {loading ? <p>Cargando...</p> : (
            <>
              {!budgetForm.id ? (
                <div className="mb-3">
                  <label className="form-label small">Cliente *</label>
                  <Select
                    classNamePrefix="select"
                    isLoading={clientsLoading}
                    options={clientOptions}
                    value={selectedClientOption}
                    placeholder="Selecciona cliente"
                    onChange={(opt) => setBudgetForm((p) => ({ ...p, clientId: opt ? opt.value : null, clientLabel: opt?.label || '' }))}
                    onInputChange={(val, action) => { if (action.action === 'input-change') setClientQuery(val); return val; }}
                    noOptionsMessage={() => 'Sin resultados'}
                    isDisabled={readOnly || clientLocked}
                  />
                </div>
              ) : (
                <div className="mb-3">
                  <label className="form-label small">Cliente</label>
                  <input className="form-control" value={budgetForm.clientLabel || `Cliente #${budgetForm.clientId || '-'}`} disabled />
                </div>
              )}

              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small">Presupuesto *</label>
                  <input className="form-control" value={budgetForm.presupuesto} onChange={(e) => setBudgetForm((p) => ({ ...p, presupuesto: e.target.value }))} disabled={readOnly} />
                  {numberLoading && <div className="text-muted small mt-1">Generando número...</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Fecha</label>
                  <input className="form-control" type="date" value={budgetForm.fecha} onChange={(e) => setBudgetForm((p) => ({ ...p, fecha: e.target.value }))} disabled={readOnly} />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Válido hasta</label>
                  <input className="form-control" type="date" value={budgetForm.validUntil || ''} onChange={(e) => setBudgetForm((p) => ({ ...p, validUntil: e.target.value }))} disabled={readOnly} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Estado</label>
                  <select className="form-select" value={budgetForm.estado} onChange={(e) => setBudgetForm((p) => ({ ...p, estado: e.target.value }))} disabled={readOnly}>
                    <option>Borrador</option>
                    <option>Enviado</option>
                    <option>Pendiente</option>
                    <option>Aceptado</option>
                    <option>Rechazado</option>
                    <option>Caducado</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small">Notas y condiciones</label>
                  <textarea className="form-control" rows="2" value={budgetForm.footerText} onChange={(e) => setBudgetForm((p) => ({ ...p, footerText: e.target.value }))} placeholder="Condiciones, forma de pago, validez, notas... (aparece al pie del PDF)" disabled={readOnly} />
                </div>
              </div>

              {budgetForm.id ? (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Líneas de presupuesto</h6>
                    {loadingBudgetLines && <span className="text-muted small">Cargando...</span>}
                  </div>
                  {readOnly ? (
                    <>
                      <div className="alert alert-info mb-2">Las líneas se gestionan en Dolibarr. Aquí puedes consultarlas.</div>
                      {renderBudgetLinesTable(false)}
                    </>
                  ) : (
                    <>
                      <div className="row g-2 mb-2">
                        <div className="col-md-3">
                          <label className="form-label small mb-0">Concepto</label>
                          <input className="form-control" value={budgetLineForm.concepto} onChange={(e) => setBudgetLineForm((p) => ({ ...p, concepto: e.target.value }))} />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label small mb-0">Periodo inicio</label>
                          <input className="form-control" type="date" value={budgetLineForm.periodStart} onChange={(e) => setBudgetLineForm((p) => ({ ...p, periodStart: e.target.value }))} />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label small mb-0">Periodo fin</label>
                          <input className="form-control" type="date" value={budgetLineForm.periodEnd} onChange={(e) => setBudgetLineForm((p) => ({ ...p, periodEnd: e.target.value }))} />
                        </div>
                        <div className="col-md-1">
                          <label className="form-label small mb-0">Cant.</label>
                          <input className="form-control" type="number" step="0.01" value={budgetLineForm.cantidad} onChange={(e) => setBudgetLineForm((p) => ({ ...p, cantidad: Number(e.target.value) }))} />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label small mb-0">Precio</label>
                          <input className="form-control" type="number" step="0.01" value={budgetLineForm.precio} onChange={(e) => setBudgetLineForm((p) => ({ ...p, precio: Number(e.target.value) }))} />
                        </div>
                        <div className="col-md-1">
                          <label className="form-label small mb-0">Dto.%</label>
                          <input className="form-control" type="number" step="0.01" min="0" max="100" value={budgetLineForm.descuento} onChange={(e) => setBudgetLineForm((p) => ({ ...p, descuento: Number(e.target.value) }))} />
                        </div>
                        <div className="col-md-1">
                          <label className="form-label small mb-0">IVA</label>
                          <select className="form-select" value={budgetLineForm.tvaTx} onChange={(e) => setBudgetLineForm((p) => ({ ...p, tvaTx: Number(e.target.value) }))}>
                            <option value={21}>21%</option>
                            <option value={10}>10%</option>
                            <option value={4}>4%</option>
                            <option value={0}>0%</option>
                          </select>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted small">Subtotal línea (neto): {formatCurrency(lineFormTotals.net)}</span>
                        <div>
                          {budgetLineForm.id && <button className="btn btn-sm btn-outline-secondary me-2" type="button" onClick={() => setBudgetLineForm(emptyLineForm)}>Cancelar</button>}
                          <button className="btn btn-sm btn-primary" type="button" onClick={saveBudgetLine}>{budgetLineForm.id ? 'Actualizar línea' : 'Añadir línea'}</button>
                        </div>
                      </div>
                      {renderBudgetLinesTable(true)}
                    </>
                  )}

                  {/* Totales */}
                  <div className="d-flex justify-content-end mt-3">
                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between"><span className="text-muted">Base imponible</span><strong>{formatCurrency(displayTotals.subtotalValue)}</strong></div>
                      {rateKeys.length > 1 && !readOnly ? rateKeys.map((r) => (
                        <div className="d-flex justify-content-between" key={r}><span className="text-muted">IVA {r}%</span><span>{formatCurrency(displayTotals.byRate[r].iva)}</span></div>
                      )) : (
                        <div className="d-flex justify-content-between"><span className="text-muted">IVA{rateKeys.length === 1 ? ` ${rateKeys[0]}%` : ''}</span><span>{formatCurrency(displayTotals.ivaValue)}</span></div>
                      )}
                      <hr className="my-1" />
                      <div className="d-flex justify-content-between fs-6"><strong>Total</strong><strong className="text-primary">{formatCurrency(displayTotals.totalValue)}</strong></div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-info mt-3">Guarda el presupuesto para poder añadir líneas y adjuntos.</div>
              )}

              {budgetForm.id && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Adjuntos</h6>
                    {attachmentsLoading && <span className="text-muted small">Cargando...</span>}
                  </div>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-6">
                      <input className="form-control" type="file" disabled={uploadingAttachment} onChange={uploadAttachment} />
                      <div className="text-muted small mt-1">Marca un archivo como "PDF presupuesto" para que sustituya al PDF generado al descargar/enviar.</div>
                    </div>
                  </div>
                  {attachmentsError && <div className="text-danger small mt-2">{attachmentsError}</div>}
                  {attachments.length ? (
                    <ul className="list-group list-group-flush mt-2">
                      {attachments.map((file) => (
                        <li key={file.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div>
                            {file.filename}{' '}
                            <span className="text-muted small">{file.size ? `(${formatFileSize(file.size)})` : ''}</span>
                            {file.is_quote_pdf && <span className="badge bg-primary ms-2">PDF presupuesto</span>}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <div className="form-check form-switch m-0" title="Usar este archivo como PDF del presupuesto">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={Boolean(file.is_quote_pdf)}
                                disabled={markingId === file.id}
                                onChange={() => toggleQuotePdf(file)}
                              />
                              <label className="form-check-label small text-muted">PDF presupuesto</label>
                            </div>
                            <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => downloadFile(`/api/clients/budget-attachments/${file.id}/download`, file.filename)}>Descargar</button>
                            <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => deleteAttachment(file.id)}>Eliminar</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-muted small">Todavía no hay adjuntos.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {budgetForm.id && (
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-primary btn-sm" onClick={() => previewPdf(`/api/clients/budgets/${budgetForm.id}/pdf`)}>Ver PDF</button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => downloadFile(`/api/clients/budgets/${budgetForm.id}/pdf`, `${budgetForm.presupuesto || 'presupuesto'}.pdf`)}>Descargar PDF</button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => downloadFile(`/api/clients/budgets/${budgetForm.id}/pptx`, `${budgetForm.presupuesto || 'presupuesto'}.pptx`)}>Descargar PPTX</button>
          {readOnly && (
            <button className="btn btn-outline-primary btn-sm" onClick={() => downloadFile(`/api/clients/budgets/${budgetForm.id}/dolibarr-pdf`, `${budgetForm.presupuesto || 'presupuesto'}.pdf`)}>PDF Dolibarr</button>
          )}
          <button className="btn btn-outline-success btn-sm" onClick={openEmailModal}>Enviar por email</button>
          <button className="btn btn-outline-success btn-sm" onClick={openWhatsappModal}>Enviar por WhatsApp</button>
          {!readOnly && <button className="btn btn-outline-secondary btn-sm" onClick={duplicateBudget}>Duplicar</button>}
          {!budgetForm.convertedInvoiceId && <button className="btn btn-outline-warning btn-sm" onClick={convertToInvoice}>Convertir a factura</button>}
          {clientIdParam && <Link className="btn btn-outline-secondary btn-sm" to={backTo}>Volver al cliente</Link>}
        </div>
      )}

      {emailModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar por email</h5>
                <button className="btn-close" onClick={() => setEmailModal((p) => ({ ...p, open: false }))}></button>
              </div>
              <div className="modal-body">
                {emailModal.error && <div className="alert alert-danger py-2">{emailModal.error}</div>}
                <div className="mb-2"><label className="form-label small">Para</label><input className="form-control" value={emailModal.to} onChange={(e) => setEmailModal((p) => ({ ...p, to: e.target.value }))} /></div>
                <div className="mb-2"><label className="form-label small">Asunto</label><input className="form-control" value={emailModal.subject} onChange={(e) => setEmailModal((p) => ({ ...p, subject: e.target.value }))} /></div>
                {templates.length > 0 && (
                  <div className="mb-2">
                    <label className="form-label small">Plantilla</label>
                    <select className="form-select" value={emailModal.templateId} onChange={(e) => { const t = templates.find((x) => String(x.id) === e.target.value); setEmailModal((p) => ({ ...p, templateId: e.target.value, bodyHtml: t?.body_html || p.bodyHtml })); }}>
                      <option value="">(Sin plantilla)</option>
                      {templates.map((t) => <option key={t.id} value={String(t.id)}>{t.name || `Plantilla #${t.id}`}</option>)}
                    </select>
                  </div>
                )}
                <div className="text-muted small">Se adjuntará el PDF (el archivo marcado como "PDF presupuesto" si lo hay).</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setEmailModal((p) => ({ ...p, open: false }))}>Cancelar</button>
                <button className="btn btn-primary" onClick={sendEmail} disabled={emailModal.sending}>{emailModal.sending ? 'Enviando...' : 'Enviar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {whatsappModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar por WhatsApp</h5>
                <button className="btn-close" onClick={() => setWhatsappModal((p) => ({ ...p, open: false }))}></button>
              </div>
              <div className="modal-body">
                {whatsappModal.error && <div className="alert alert-danger py-2">{whatsappModal.error}</div>}
                <div className="mb-2"><label className="form-label small">Teléfono</label><input className="form-control" value={whatsappModal.phone} onChange={(e) => setWhatsappModal((p) => ({ ...p, phone: e.target.value }))} placeholder="+34..." /></div>
                <div className="text-muted small">Se enviará el PDF (el archivo marcado como "PDF presupuesto" si lo hay) por la plantilla configurada.</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setWhatsappModal((p) => ({ ...p, open: false }))}>Cancelar</button>
                <button className="btn btn-primary" onClick={sendWhatsapp} disabled={whatsappModal.sending}>{whatsappModal.sending ? 'Enviando...' : 'Enviar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetFormPage;
